import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  contestantLabel,
  formatMs,
  formatUsd,
  sumCosts,
  summarizeRunByModel,
  TASK_META,
  type ModelLabCaseSummary,
  type ModelLabResultSummary,
  type Task,
} from '@/lib/model-lab'

interface Props {
  readonly contestantKeys: readonly string[]
  readonly tasks: readonly Task[]
  readonly cases: readonly ModelLabCaseSummary[]
  readonly results: readonly ModelLabResultSummary[]
}

/** Whole-run totals per model, across every decision type tested. */
export function RunSummary({ contestantKeys, tasks, cases, results }: Props) {
  const rows = summarizeRunByModel(contestantKeys, tasks, cases, results)
  const priced = rows.map((r) => r.totalSpent).filter((v): v is number => v !== null)
  const cheapest = priced.length > 1 ? Math.min(...priced) : null

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Spend per model — whole run</CardTitle>
        <CardDescription>
          Estimated at list prices from the tokens each provider reported, across every decision type. Your provider
          dashboards are the source of truth.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              {tasks.map((task) => (
                <TableHead key={task} className="text-right">
                  {TASK_META[task].label}
                </TableHead>
              ))}
              <TableHead className="text-right">Total spent</TableHead>
              <TableHead className="text-right">Calls</TableHead>
              <TableHead className="text-right">Failed</TableHead>
              <TableHead className="text-right">Avg response</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.contestantKey}>
                <TableCell className="font-mono text-xs">{contestantLabel(r.contestantKey)}</TableCell>
                {tasks.map((task) => (
                  <TableCell key={task} className="text-right text-muted-foreground">
                    {formatUsd(r.spentByTask[task] ?? null)}
                  </TableCell>
                ))}
                <TableCell
                  className={`text-right font-semibold ${r.totalSpent !== null && r.totalSpent === cheapest ? 'text-emerald-700' : ''}`}
                >
                  {formatUsd(r.totalSpent)}
                </TableCell>
                <TableCell className="text-right">{r.calls}</TableCell>
                <TableCell className={`text-right ${r.failed > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>{r.failed}</TableCell>
                <TableCell className="text-right">{formatMs(r.avgLatencyMs)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-medium">All models</TableCell>
              {tasks.map((task) => (
                <TableCell key={task} className="text-right">
                  {formatUsd(sumCosts(rows.map((r) => ({ cost_usd: r.spentByTask[task] ?? null }))))}
                </TableCell>
              ))}
              <TableCell className="text-right font-semibold">{formatUsd(sumCosts(results))}</TableCell>
              <TableCell className="text-right">{results.length}</TableCell>
              <TableCell className="text-right">{rows.reduce((n, r) => n + r.failed, 0)}</TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
      </CardContent>
    </Card>
  )
}
